import React from 'react';
import { Star, Users, Calendar, Award, MessageSquare, Instagram, Facebook, Globe, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
export function TrustIndex() {
  const metrics = [{
    label: 'Global Enthusiasts',
    value: '12k+',
    icon: Users
  }, {
    label: 'Curated Events',
    value: '450+',
    icon: Calendar
  }, {
    label: 'Industry Awards',
    value: '24',
    icon: Award
  }, {
    label: 'Public Reviews',
    value: '1,800+',
    icon: MessageSquare
  }];
  const platforms = [{
    name: 'Google',
    score: '4.9',
    reviews: '842',
    icon: Globe
  }, {
    name: 'Facebook',
    score: '5.0',
    reviews: '420',
    icon: Facebook
  }, {
    name: 'Instagram',
    score: 'Engagement',
    reviews: 'High',
    icon: Instagram
  }];
  const feedback = [{
    platform: 'Google',
    user: 'Julianne M.',
    text: 'The private tasting was an absolute masterclass in hospitality. The attention to detail and the depth of wine education provided by Cork Dorx is unparalleled.',
    date: '2 days ago'
  }, {
    platform: 'Facebook',
    user: 'Robert H.',
    text: 'Interactive, engaging, and incredibly sophisticated. We booked a corporate team-building event and it exceeded every expectation. A true luxury experience.',
    date: '1 week ago'
  }];
  return <section className="bg-[#2D0909] py-32 px-6 overflow-hidden relative" data-id="element-124">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[#4A0E0E]/20 skew-x-12 translate-x-1/4 pointer-events-none" data-id="element-125" />

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-126">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32" data-id="element-127">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-128">
            <span className="text-[#D4AF37] font-bold uppercase tracking-[0.4em] text-xs mb-6 block" data-id="element-129">
              Reputation & Trust
            </span>
            <h2 className="text-white text-5xl md:text-7xl mb-8 leading-tight" data-id="element-130">
              What Our <br data-id="element-131" />
              <span className="text-[#D4AF37] italic" data-id="element-132">Customers Say</span>
            </h2>
            <p className="text-white/60 text-lg max-w-xl leading-relaxed" data-id="element-133">
              Our reputation is built on the voices of our community. We take
              pride in maintaining the highest levels of transparency and
              excellence across every experience we curate.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white/5 backdrop-blur-xl border border-white/10 p-12 rounded-[2rem]" data-id="element-134">
            <div className="space-y-10" data-id="element-135">
              {platforms.map((platform, idx) => <div key={platform.name} className="flex items-center justify-between group" data-id="element-136">
                  <div className="flex items-center gap-6" data-id="element-137">
                    <div className="p-4 bg-white/5 rounded-2xl text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#2D0909] transition-all duration-500" data-id="element-138">
                      <platform.icon size={28} data-id="element-139" />
                    </div>
                    <div data-id="element-140">
                      <h4 className="text-white font-serif text-xl mb-1" data-id="element-141">
                        {platform.name}
                      </h4>
                      <p className="text-white/40 text-xs uppercase tracking-widest" data-id="element-142">
                        {platform.reviews} Verified Reviews
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-143">
                    <div className="flex items-center gap-2 text-[#D4AF37] mb-1" data-id="element-144">
                      <Star size={16} fill="currentColor" data-id="element-145" />
                      <span className="text-2xl font-serif font-bold text-white" data-id="element-146">
                        {platform.score}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 mb-32" data-id="element-147">
          {metrics.map((metric, idx) => <motion.div key={metric.label} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: idx * 0.1
        }} className="text-center p-8 rounded-3xl bg-white/5 border border-white/5 hover:border-[#D4AF37]/30 transition-colors" data-id="element-148">
              <div className="text-[#D4AF37] mb-6 flex justify-center" data-id="element-149">
                <metric.icon size={32} strokeWidth={1} data-id="element-150" />
              </div>
              <div className="text-5xl font-serif text-white mb-3" data-id="element-151">
                {metric.value}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold" data-id="element-152">
                {metric.label}
              </div>
            </motion.div>)}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-32" data-id="element-153">
          {feedback.map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: idx * 0.2
        }} className="relative p-12 bg-[#4A0E0E]/30 rounded-[3rem] border border-white/5 overflow-hidden group" data-id="element-154">
              <Quote className="absolute -top-4 -right-4 text-white/5 w-40 h-40" data-id="element-155" />

              <div className="flex items-center gap-1 mb-8" data-id="element-156">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} fill="#D4AF37" className="text-[#D4AF37]" data-id="element-157" />)}
              </div>

              <p className="text-2xl font-serif text-white mb-10 leading-relaxed italic relative z-10" data-id="element-158">
                "{item.text}"
              </p>

              <div className="flex justify-between items-center relative z-10" data-id="element-159">
                <div className="flex items-center gap-4" data-id="element-160">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#2D0909] text-sm font-bold" data-id="element-161">
                    {item.user[0]}
                  </div>
                  <div data-id="element-162">
                    <h5 className="text-white font-bold tracking-wide" data-id="element-163">
                      {item.user}
                    </h5>
                    <p className="text-[10px] uppercase tracking-widest text-white/40" data-id="element-164">
                      Verified Guest • {item.platform}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Social Proof Badges */}
        <div className="pt-20 border-t border-white/10 flex flex-wrap justify-center items-center gap-16 opacity-40 hover:opacity-100 transition-all duration-1000" data-id="element-165">
          <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#D4AF37]" data-id="element-166">
            Recognized By
          </div>
          <div className="font-serif text-2xl text-white/80 hover:text-white transition-colors" data-id="element-167">
            WineSpectator
          </div>
          <div className="font-serif text-2xl text-white/80 hover:text-white transition-colors" data-id="element-168">
            Decanter
          </div>
          <div className="font-serif text-2xl text-white/80 hover:text-white transition-colors" data-id="element-169">
            JamesSuckling
          </div>
          <div className="font-serif text-2xl text-white/80 hover:text-white transition-colors" data-id="element-170">
            Vivino
          </div>
        </div>
      </div>
    </section>;
}